import { FunctionComponent, useCallback, useState } from 'react';
import cn from 'classnames';
import css from './TribesPost.module.less';
import Image from '@components/Image';
import TribeClaimsList from '@components/TribeClaimsList';
import UserShortCard from '@components/UserShortCard';
import { goTo, getPath } from '@helpers/navigation';
import {
  CalendarOutlined,
  EditOutlined,
  RightCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { FULL_TIME_FORMAT } from '@config/format';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../TribesPage.content.json';
import { Row, Tag, Popconfirm } from 'antd';
import { Link } from 'react-router-dom';
import routes from '@config/routes';
import { deleteTribe, blockTribe, unblockTribe } from '@stores/tribes';
import { openModal } from '@stores/modals';
import { ITribes } from '@interfaces/ITribes';
interface TribesPostProps {
  tribes: ITribes;
}

const TribesPost: FunctionComponent<TribesPostProps> = ({ tribes }) => {
  const content = useTranslate(componentContent);
  const filterByUser = () => goTo('userDetail', { userId: tribes.owner.id });

  const [isClaimsOpen, setIsClaimsOpen] = useState(false);

  const toggleClaims = () => setIsClaimsOpen((p) => !p);

  const editTribesAbout = useCallback(() => {
    openModal({ name: 'tribes', data: tribes });
  }, [tribes]);

  return (
    <div className={css.root}>
      <div className={css.images}>
        <Image
          className={css.images}
          url={tribes.logo?.url}
        />
      </div>
      <div className={css.content}>
        <div className={css.header}>
          <div className={css.headerUserName}>
            <Row>
              <UserShortCard
                user={tribes.owner}
                onClick={filterByUser}
              />
              {tribes.isBlocked ? (
                <Popconfirm
                  title={content.unblock_tribe}
                  okText='OK'
                  cancelText={content.cancel}
                  placement='bottom'
                  icon={<ExclamationCircleOutlined />}
                  onConfirm={() => {
                    unblockTribe(String(tribes.id));
                  }}
                >
                  <Tag
                    color='red'
                    className={css.isBlocked}
                  >
                    {content.status_block_tribe}
                  </Tag>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={content.block_tribe}
                  okText='OK'
                  cancelText={content.cancel}
                  placement='bottom'
                  icon={<ExclamationCircleOutlined />}
                  onConfirm={() => {
                    blockTribe(String(tribes.id));
                  }}
                >
                  <Tag
                    color='green'
                    className={css.isBlocked}
                  >
                    {content.status_unblock_tribe}
                  </Tag>
                </Popconfirm>
              )}
            </Row>
            <div className={css.meta}>
              <span className={css.metaIcon}>
                <CalendarOutlined />
              </span>
              <span>{moment(tribes.created).format(FULL_TIME_FORMAT)}</span>
            </div>
          </div>
        </div>
        <div className={css.body}>
          <div className={css.info}>
            <span className={css.title}>{content.title}:</span>
            {tribes.name}
          </div>

          <div className={css.info}>
            <span className={css.title}>{content.game}:</span>
            <Tag color='orange'>{tribes?.game?.name}</Tag>
          </div>

          <div className={css.about}>
            <div className={css.info}>
              <span className={css.title}>{content.about}:</span>
              {tribes.about}
            </div>

            {tribes?.privacyType === 'PRIVACY' && (
              <div className={css.info}>
                <span className={css.title}>{content.type_tribe}:</span>
                <Tag color='orange'>{content.close_tribe}</Tag>
              </div>
            )}

            {tribes?.privacyType === 'PUBLIC' && (
              <div className={css.info}>
                <span className={css.title}>{content.type_tribe}:</span>
                <Tag color='green'>{content.open_tribe}</Tag>
              </div>
            )}

            <div className={css.info}>
              {tribes?.claimsCount ?? 0 > 0 ? (
                <a
                  className='clickable'
                  onClick={toggleClaims}
                >
                  <span className={css.title}>{content.claims_count}:</span>
                  <span className={css.claimsCount}>{tribes?.claimsCount ?? 0}</span>
                </a>
              ) : (
                <>
                  <span className={css.title}>{content.claims_count}:</span>
                  <span className={css.claimsCount}>{tribes?.claimsCount ?? 0}</span>
                </>
              )}
            </div>

            {isClaimsOpen && (
              <div className={css.claimsPanel}>
                <TribeClaimsList tribeId={String(tribes.id)} />
              </div>
            )}

            <div className={css.info}>
              <span className={css.title}>{content.message_count}:</span>
              {tribes?.messagesCount ?? 0}
            </div>

            <div className={css.info}>
              <span className={css.title}>{content.monthly_message_count}:</span>
              {tribes?.monthlyMessagesCount ?? 0}
            </div>

            <div className={css.info}>
              <span className={css.title}>{content.last_message}:</span>
              {tribes?.lastMessageSentDateTime
                ? moment(tribes.lastMessageSentDateTime).format(FULL_TIME_FORMAT)
                : '—'}
            </div>
          </div>

          <div className={cn(css.metaLine, css.meta)}>
            <span className={css.metaIcon}>
              <RightCircleOutlined />
            </span>
            <Link
              to={getPath(routes.chatDetail.name, { chatId: tribes.chatId })}
              className='clickable'
            >
              {content.show_more}
            </Link>
          </div>

          <div className={cn(css.metaLine, css.meta)}>
            <span className={css.metaIcon}>
              <RightCircleOutlined />
            </span>
            <Link
              to={getPath(routes.tribesMembers.name, { tribeId: tribes.id })}
              className='clickable'
              // target='_blank'
            >
              {content.show_members}: {tribes.membersCount}
            </Link>
          </div>
        </div>
        <div className={css.footer}>
          <div className={cn(css.metaLine, css.meta)}>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <EditOutlined />
              </span>
              <span
                className='clickable'
                onClick={editTribesAbout}
              >
                {content.edit_description}
              </span>
            </div>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <StopOutlined />
              </span>
              <span
                className='clickable'
                onClick={() => {
                  deleteTribe(tribes.id);
                }}
              >
                {content.delete}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TribesPost;
